import { useState } from 'react'
import { Trace } from '../lib/trace'
import { api, unlock, type Child } from '../api'
import { theme } from '../theme'
import { Mascot } from './Mascot'
import { DigitEntry, Modal } from './GrownUpGate'
import { sfx } from '../lib/sound'

/**
 * A child chooses their own PIN, with a grown-up there (D-065).
 *
 * Shown after the grown-up PIN, never before: making a PIN for a child is
 * the same power as resetting one, so it needs a grown-up. Used for a child
 * who has no PIN yet (Charles and Mark, made before PINs existed) and for a
 * child who forgot theirs.
 *
 * A PIN you chose is one you remember. Four random digits read out once are
 * not, and a forgotten PIN means fetching a grown-up every time.
 */
export function ChildPinMaker({
  child, onDone, onCancel,
}: { child: Child; onDone: () => void; onCancel: () => void }) {
  const [first, setFirst] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const confirm = async (pin: string) => {
    if (pin !== first) {
      setMessage('Those two were different. Choose again.')
      setFirst(null)
      throw new Error('mismatch')
    }
    setBusy(true)
    try {
      const made = await api.setChildPin(child.learner_id, pin)
      unlock.setChild(child.learner_id, made.ticket)
      sfx.correct()
      onDone()
    } catch (err) {
      setMessage((err as { detail?: string } | null)?.detail || 'That did not work. Try again.')
      setFirst(null)
      throw err
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal label={`${child.display_name}'s new PIN`} onCancel={onCancel}>
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 2 }}>
        <Mascot size={58} mood="cheer" label="" />
      </div>
      <h2 style={{ margin: '4px 0 2px', fontSize: 22, color: theme.text }}>
        {child.display_name}, make your PIN
      </h2>
      <p style={{ margin: '0 0 12px', color: theme.muted, fontSize: 15, lineHeight: 1.45 }}>
        <Trace id="T25.pin_hash" writes title="stored only as a hash; also saves a ticket to T28 access_grant">
          {/* The child reads this screen, and children's screens stay English. */}
          Four numbers only you know. Not 1234, and not all the same.
        </Trace>
      </p>
      {first === null ? (
        <DigitEntry
          key="first"
          prompt="Choose 4 numbers."
          min={4} max={4}
          busy={busy}
          message={message}
          onDigit={() => setMessage(null)}
          onSubmit={(pin) => { setFirst(pin) }}
        />
      ) : (
        <DigitEntry
          key="again"
          prompt="Type them once more."
          min={4} max={4}
          busy={busy}
          message={message}
          onDigit={() => setMessage(null)}
          onSubmit={confirm}
        />
      )}
    </Modal>
  )
}
